import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '@/styles/GraphStyles.scss'
import * as Sentry from '@sentry/react';

// CustomTooltip component defined outside to avoid recreating on each render
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-recharts-tooltip">
                <div>
                    <strong style={{ color: "#F63040" }}>{label}</strong>
                </div>
                {payload.map((entry, idx) => (
                    <div key={idx} style={{ color: entry.color }}>
                        {entry.name === 'current' ? 'Current period' : 'Previous period'}: {entry.value} incidents
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const OpsIncidentGraph = ({ height = undefined }) => {
    const { apiFetch } = useAuth();
    const [incidentData, setIncidentData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const now = new Date();
    const period = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;

    // If no height is provided (undefined), use 100% to fill container
    const useFlexHeight = height === undefined;
    const validHeight = typeof height === 'number' && height > 0 ? height : 400;

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const monthAbbr = {
        January: 'Jan', February: 'Feb', March: 'Mar', April: 'Apr',
        May: 'May', June: 'Jun', July: 'Jul', August: 'Aug',
        September: 'Sep', October: 'Oct', November: 'Nov', December: 'Dec'
    };

    // Fetch data directly in useEffect instead of calling a function
    useEffect(() => {
        if (!period) return;

        const fetchData = async () => {
            try {
                const response = await apiFetch(`/api/Ops/GetOpsIncidentReports?Period=${period}`);
                const data = await response.json();
                setIncidentData(data.data);
                setIsLoading(false);
            } catch (e) {
                Sentry.captureException(e);
                console.error("Error fetching incident data:", e);
                setError("Error loading incident data");
                setIsLoading(false);
            }
        };

        fetchData();
    }, [period, apiFetch]);

    /**
     * Builds a rolling 12-month window ending at the current month.
     * Returns an array of { year, month } objects in chronological order.
     */
    const buildRollingMonths = () => {
        const now = new Date();
        const currentMonthIndex = now.getMonth(); // 0-based
        const currentYear = now.getFullYear();
        const months = [];

        for (let i = 11; i >= 0; i--) {
            const date = new Date(currentYear, currentMonthIndex - i, 1);
            months.push({
                year: date.getFullYear(),
                month: monthNames[date.getMonth()]
            });
        }
        return months;
    };

    const prepareChartData = () => {
        if (!incidentData) return [];

        const rollingCurrent = buildRollingMonths();

        // Previous period is the 12 months before the current rolling window
        const firstCurrent = rollingCurrent[0];
        const firstCurrentDate = new Date(
            firstCurrent.year,
            monthNames.indexOf(firstCurrent.month),
            1
        );
        const rollingPrevious = [];
        for (let i = 12; i >= 1; i--) {
            const date = new Date(firstCurrentDate.getFullYear(), firstCurrentDate.getMonth() - i, 1);
            rollingPrevious.push({
                year: date.getFullYear(),
                month: monthNames[date.getMonth()]
            });
        }

        return rollingCurrent.map((cur, idx) => {
            const prev = rollingPrevious[idx];
            const currentKey = `${cur.year} ${cur.month}`;
            const previousKey = `${prev.year} ${prev.month}`;
            return {
                month: `${monthAbbr[cur.month]} ${cur.year}`,
                current: incidentData.currentPeriodResult?.[currentKey] || 0,
                previous: incidentData.previousPeriodResult?.[previousKey] || 0
            };
        });
    };

    if (isLoading) return <div>Loading incident data...</div>;
    if (error) return <div>Error: {error}</div>;

    const chartData = prepareChartData();

    // Y-axis: Only whole numbers, based on max of current/previous
    const maxDataY = chartData.length
        ? Math.max(...chartData.map(d => Math.max(d.current, d.previous)), 1)
        : 1;
    const minY = 0;
    const step = 1;
    const maxTick = Math.ceil(maxDataY / step) * step;
    const ticks = [];
    for (let t = minY; t <= maxTick; t += step) {
        ticks.push(t);
    }

    // Calculate content height accounting for heading (approximately 40px)
    const GRAPH_HEADING_HEIGHT = 40;
    const MIN_GRAPH_HEIGHT = 300;
    const MIN_CONTENT_HEIGHT = MIN_GRAPH_HEIGHT - GRAPH_HEADING_HEIGHT;

    return (
        <div className="graph-box" style={useFlexHeight ? { height: '100%' } : { height: `${validHeight}px`, minHeight: `${MIN_GRAPH_HEIGHT}px` }}>
            <div className="graph-heading">
                Monthly Armed Robberies Compared to Prior Year
            </div>
            <div className="graph-content" style={{ height: `calc(100% - ${GRAPH_HEADING_HEIGHT}px)`, minHeight: `${MIN_CONTENT_HEIGHT}px` }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={{ top: 20, right: 40, left: 10, bottom: 5 }}
                    >
                        <CartesianGrid stroke="#000" strokeDasharray="0" horizontal={true} vertical={false} fill="#f4f4f4" />
                        <XAxis
                            dataKey="month"
                            interval={0}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                        />
                        <YAxis domain={[minY, maxTick]}
                            ticks={ticks}
                            label={{
                                value: 'month',
                                angle: -90,
                                position: 'left',
                                offset: 35,
                                style: { textAnchor: 'middle', fontSize: 14, fill: '#555' }
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            formatter={(value) => value === 'current' ? 'Current period' : 'Previous period'}
                        />
                        <Line
                            type="monotone"
                            dataKey="current"
                            stroke="#F63040"
                            strokeWidth={2}
                            dot={false}
                            name="current"
                        />
                        <Line
                            type="monotone"
                            dataKey="previous"
                            stroke="#57565B"
                            strokeWidth={2}
                            name="previous"
                            dot={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default OpsIncidentGraph;